/**
 * PwaInstallBanner — subtle, dismissible install nudge.
 *
 * Design principles (per latest UX direction):
 *   1. **Benefit-first copy**: lead with "faster, full-screen, offline" — not
 *      "tap menu → install" multi-step instructions.
 *   2. **One-click on Android/Chromium**: native `beforeinstallprompt` dialog
 *      triggers immediately when user clicks "Install" — no manual steps.
 *   3. **Hide aggressively if installed**: respects `display-mode: standalone`,
 *      `navigator.standalone`, the `appinstalled` event, AND
 *      `getInstalledRelatedApps()` where supported. Once installed, banner is
 *      hidden for 365 days (effectively forever).
 *   4. **Subtle, smaller, less prominent**: takes less vertical space than the
 *      old version. No "desktop fallback" multi-step instructions block.
 *   5. **iOS — keep brief**: one-line nudge ("Add Filmistani to your home
 *      screen") with a small Share icon hint. Hidden after first dismiss.
 */
import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "fm.pwa.dismiss.v2";
const INSTALLED_KEY = "fm.pwa.installed.v1";
// Banner re-appearance window:
//   - Dismissed (user said "not now"): 90 days before re-asking
//   - Already installed: 365 days (effectively permanent)
const DISMISS_DAYS = 90;
const INSTALLED_DAYS = 365;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch { return false; }
}

function wasMarkedInstalled() {
  try {
    const ts = parseInt(localStorage.getItem(INSTALLED_KEY) || "0", 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < INSTALLED_DAYS;
  } catch { return false; }
}

function markInstalled() {
  try { localStorage.setItem(INSTALLED_KEY, String(Date.now())); } catch (e) { /* ignore */ }
}

export default function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;        // already inside the installed app
    if (wasMarkedInstalled()) return;  // we previously saw an install event
    if (recentlyDismissed()) return;   // user said "not now" recently

    // Best-effort: ask the browser if any related apps are already installed.
    // Only Chromium-on-Android supports this; everywhere else it just returns [].
    let cancelled = false;
    (async () => {
      try {
        if (typeof navigator?.getInstalledRelatedApps === "function") {
          const installed = await navigator.getInstalledRelatedApps();
          if (installed && installed.length > 0) {
            markInstalled();
            return; // don't show banner — already installed
          }
        }
      } catch (e) { /* ignore */ }

      if (cancelled) return;

      if (isIOS()) {
        // iOS Safari: no `beforeinstallprompt` API. Show a brief one-line
        // nudge after a short delay so it doesn't pop immediately on load.
        const t = setTimeout(() => {
          if (!cancelled) { setIosMode(true); setVisible(true); }
        }, 4000);
        return () => clearTimeout(t);
      }

      // Android / Chromium — listen for native install opportunity.
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setVisible(true);
      };
      window.addEventListener("beforeinstallprompt", handler);

      // Also listen for the `appinstalled` event — fires once the user
      // completes the install. We mark it locally so we never re-prompt.
      const onInstalled = () => {
        markInstalled();
        setVisible(false);
        setDeferredPrompt(null);
      };
      window.addEventListener("appinstalled", onInstalled);
    })();

    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        markInstalled();
        setVisible(false);
      }
    } catch (e) { /* user cancelled */ }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <section className="px-6 lg:px-10 mt-8" data-testid="pwa-install-banner">
      <div className="max-w-[1440px] mx-auto">
        <div className="relative overflow-hidden rounded-xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.05] transition-colors px-4 py-3 sm:px-5 sm:py-3.5 flex items-center gap-3 sm:gap-4">
          {/* icon */}
          <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#E50914]/15 ring-1 ring-[#E50914]/30 flex items-center justify-center">
            <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFB7B7]" />
          </div>

          {/* benefit-first copy */}
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-[15px] font-semibold text-white/95 leading-tight">
              {iosMode ? "Filmistani on your home screen" : "Faster, full-screen, offline"}
            </p>
            <p className="text-[11px] sm:text-xs text-white/45 mt-0.5 truncate">
              {iosMode
                ? "Tap Share → Add to Home Screen. No app-store install."
                : "One-click install — no app store, no signup."}
            </p>
          </div>

          {/* CTA */}
          <div className="flex items-center gap-1.5 shrink-0">
            {!iosMode && (
              <button
                onClick={install}
                data-testid="pwa-install-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-white/10 hover:bg-[#E50914] text-white text-xs font-bold transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Install
              </button>
            )}
            <button
              onClick={dismiss}
              data-testid="pwa-dismiss-button"
              aria-label="Dismiss install prompt"
              className="p-1.5 rounded-md hover:bg-white/5 text-white/40 hover:text-white/80 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
